<template>
    <GenericTable :Headers="phoneColumns" :Rows="phoneList" :EnableSelection="true" :EnablePagination="true" :ShowCountRowNumber="true">

        <template #toolbar-right="{ SelectedRows }">
            <b-button @click="deletePhones(SelectedRows)" v-b-tooltip.noninteractive.hover :title="$t('Delete')" :variant="Variant" 
                :disabled="!$store.getters['CheckAction']('Delete', undefined, Action)"
                :class="'btn-circle border-0 p-0 text-' + Variant + '-light'">
                <b-icon icon="x" />
            </b-button>
        </template>

        <!-- Boutons d'action sur un utilisateur situé à gauche des colonnes -->
        <template #menu-left="{ row }">
            <b-button @click="deletePhones([row])" v-b-tooltip.noninteractive.hover :title="$t('Delete')" variant="outline-danger"
                :disabled="!$store.getters['CheckAction']('Delete', undefined, Action)"
                class="btn-circle border-0 p-0">
                <b-icon icon="x" />
            </b-button>
        </template> 

        <template #column(IsActive)="{ row }">
            <b-link v-if="$store.getters['CheckAction']('Write', undefined, Action)" @click.prevent.stop="toggleState(row)">
                <b-icon v-if="row.IsActive !== null" icon="circle-fill" :variant="!row.IsActive ? 'danger' : 'success'"/>
            </b-link>
        </template>
    </GenericTable>
</template>

<script lang="ts">
/** Importe les décorateurs */
import { Component, InjectReactive, Prop } from 'vue-property-decorator';
import Vue from 'vue';

/** Types custom importé depuis /types */
import Dictionary from '@/types/Dictionary';

/** Fichiers de traductions */
import locales from "./locales/PhonesList.json";

/** Accès webAPI */
import axios, { AxiosResponse } from 'axios';
import { API_URL } from '@/App.settings';

/** Composant d'affichage des nodes */
import GenericTable, { HeaderCustom } from '@/components/commons/Generics/GenericTable.vue';
import { UserInformation } from './UserInformation';
import { HeaderBoolean, HeaderDate, HeaderString, IHeader, Row } from '@/components/commons/Generics/GenericTable.vue';

/** Page de gestion des utilisateurs [Administration] */
@Component({
    components: {
        GenericTable
	},
    i18n: { messages : locales}
})
export default class PhonesList extends Vue {
    /** Variant (coloration) appliqué au composant */
    @InjectReactive()
    private Variant!: string;

    /** Action utilisée pour la gestion des droits */
    @InjectReactive()
    private Action!: string;

    @Prop({ default: () => 10 })
    /** Nombre d'éléments par page */
	public PerPage!: number;

    /** Permet la récupération d'un login via son identifiant */
    @Prop({default: () => () => ""})
    public GetLoginNameById!: (userRef: string) => string;

    /** Liste des téléphones */
    private phoneList: PhoneInformation[] = [];

    /** Liste des utilisateurs */
    private userList: UserInformation[] = []; 

    /** Liste des colonnes du tableau des utilisateurs */
    private phoneColumns: IHeader[] = [
        new HeaderString("LoginRef", this.$t('Name').toString(), { HideMenu: false, ShowColumn: true, Sort: true }),
        new HeaderDate("CreateDate", this.$t('CreatedDate').toString(), { HideMenu: false, ShowColumn: true, Sort: true }),
        new HeaderDate("ModifyDate", this.$t('ModifiedDate').toString(), { HideMenu: false, ShowColumn: true, Sort: true }),
        new HeaderString("MacAddress", this.$t('MacAddress').toString(), { HideMenu: false, ShowColumn: true, Sort: true }),
        new HeaderString("IMEICode", this.$t('IMEICode').toString(), { HideMenu: false, ShowColumn: true, Sort: true }),
        new HeaderString("OperatingSystem", this.$t('OperatingSystem').toString(), { HideMenu: false, ShowColumn: true, Sort: true }),
        new HeaderString("VersionOS", this.$t('Version').toString(), { HideMenu: false, ShowColumn: true, Sort: true }),
        new HeaderCustom("IsActive", this.$t('Status').toString(), { HideMenu: false, ShowColumn: true, Sort: true })
    ];

    /** Charge la liste des téléphone connecté avec l'application DCX Relève */
    private async loadPhoneApps(){
        this.$store.dispatch("ShowLoadingMask", this.$t('LoadingPhones'));

        /** Nettoyage de la liste sans toucher à la référence du tableau */
        this.phoneList.splice(0, this.userList.length);

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

        /** Nettoyage de la liste sans toucher à la référence du tableau */
        this.userList.splice(0, this.userList.length);
        
        const checkpointUsers = axios.post(API_URL + "Administration/Users/GetUsers", args).then((response) => {
            if(response.data){
                response.data.forEach(item =>{
                    this.userList.push(item)
                })
            }
        })

        try{
            const response : AxiosResponse<any,any> = await axios.post(API_URL + "Administration/Users/GetUserPhones", args);
            if(response.data){
                /** Optimisation du temps de chargement */
                await checkpointUsers;
                this.phoneList = response.data;
                for(let phone of this.phoneList){
                    Vue.set(phone,'LoginRef', this.getLoginByRow(phone));
                }
            }
        }catch(err){
            console.error(err);
            this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { 
                title: this.$t('Error').toString(),
                autoHideDelay: 10000,
                variant: "danger",
                solid: true,
                appendToast: true,
                toaster: "b-toaster-top-center"
            });
        }
        
        this.$store.dispatch("HideLoadingMask", {});
    }

    /** 
     * Récupère l'identifiant via une ligne
     * @param row Ligne à analyser
     */
    private getLoginByRow(row: Row){
        const phone = row as PhoneInformation;
        if(phone.LoginRef){
            const user = this.userList.find(u => u.LoginRef === phone.LoginRef)
            return user ? user.Login : phone.LoginRef;
        }
    }

    /** 
     * Permet le status du téléphone
     * @param row Ligne destinataire de l'action
     */
    private async toggleState(row: Row){
        const phone = row as PhoneInformation;

        const args: Dictionary<any> = {
            Token: Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "",
            Id: phone.Id,
            IsActive: phone.IsActive
        };

        await axios.post(API_URL + "Administration/Users/GetAutoActivatePhone", args).then((response) => {
            if(response && response.data){
                phone.IsActive = !phone.IsActive
            }
        }).catch((err) =>{
            console.error(err);
            this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { 
                title: this.$t('Error').toString(),
                autoHideDelay: 10000,
                variant: "danger",
                solid: true,
                appendToast: true,
                toaster: "b-toaster-top-center"
            });
        });
    }

    /** Suspend les applications dcx_relève */
    private async deletePhones(rows: Row[]){
        let message = [];
            /** forge du message de validation sur le modèle de celui de Sandrine */
            message.push(this.$tc('MsgConfirmPhoneDelete', rows.length).toString());

            for(let row of rows){
                const phone = row as PhoneInformation;
                message.push(this.$createElement('div',
                        [this.$createElement('b-icon', {props: {icon: 'arrow-right'}}),
                            "  " + phone.LoginRef + ' (' + phone.OperatingSystem + ')']));
            }

            message.push(this.$createElement('br'));
            message.push(this.$createElement('div', [
                this.$createElement('b-icon', {props: {icon: 'exclamation-triangle-fill', variant: "warning"}}),
                "  " + this.$t('MsgConfirmInfoDelete').toString()
            ]));

            this.$bvModal.msgBoxConfirm([this.$createElement('p', message)], {
                title: this.$t('DeletePhone').toString(),
                okVariant: 'danger',
                cancelVariant: 'light',
                okTitle: this.$t('Delete').toString(),
                cancelTitle: this.$t('No').toString(),
                hideHeaderClose: false,
                centered: true
            }).then(async value => {
                if(!value) return;
                this.$store.dispatch("ShowLoadingMask", this.$t('LoadingDelete').toString());

                for (const row of rows) {
                    const phone = row as PhoneInformation;

                    /** Suppression de l'utilisateur */
                    const args: Dictionary<any> = {};
                    args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
                    args["Id"] = row.Id;

                    // Récupération de la liste des téléphones
                    await axios.post(API_URL + "Administration/Users/DeletePhone", args).then((response) => {
                        this.$bvToast.toast(phone.LoginRef, { 
                            title: this.$t('PhoneDeleted').toString(),
                            autoHideDelay: 10000,
                            variant: "success",
                            solid: true,
                            appendToast: true,
                            toaster: "b-toaster-top-center"
                        });
                    }).catch(err =>{
                        console.log(err);
                        this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { 
                            title: this.$t('Error').toString(),
                            autoHideDelay: 10000,
                            variant: "danger",
                            solid: true,
                            appendToast: true,
                            toaster: "b-toaster-top-center"
                        });
                    })
                }

                this.$store.dispatch("HideLoadingMask", {});
                await this.loadPhoneApps();
            });
    }

    /** Initialisation du composant */
    async created(){
        await this.loadPhoneApps();
        this.$emit('loaded');
    }
}

/** Classe des informations de téléphone */
class PhoneInformation extends Row {
    LoginRef : string
    CreateDate: Date
    ModifyDate: Date
    MacAddress: string
    IMEICode: string
    OperatingSystem : string
    VersionOS: string
    IsActive: boolean
};
</script>

<style scoped>

</style>